import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import WebsiteTopBar from '../../components/WebsiteTopBar';
import StorageService from '../../services/StorageService';
import { useLocationMaster } from '../../hooks/useLocationMaster';
import { useMasterFacilityTypes } from '../../hooks/useMasterFacilityTypes';
import { useMasterFacilitySubTypes } from '../../hooks/useMasterFacilitySubTypes';
import { useMasterFacilitySubSubTypes } from '../../hooks/useMasterFacilitySubSubTypes';
import { useMasterConditionFacility } from '../../hooks/useMasterConditionFacility';
import {
  fetchPublishedInfrastructureById,
  savePostRequirementDraftAPI,
  fetchPostRequirementDraftAPI,
  submitPostRequirementAPI,
  postInfrastructureRequirementMatchAPI,
  postInfrastructureRequirementBulkEnquiriesAPI,
} from '../../services/infrastructureLeasingListing.service';
import { resolvePublicFileUrl } from '../../constants/api-urls';
import { INFRA_ASSET_SIZE_OPTIONS } from '../../constants/infrastructureLeasingAssetSizes';
import { DURATION_TYPE_OPTIONS } from '../../constants/infrastructureLeasingDurationTypes';
import { URGENCY_OPTIONS } from '../../constants/infrastructureLeasingUrgencyOptions';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/website-top-bar.scss';
import '../../assets/styles/seller_profile.scss';
import '../../assets/styles/infrastructure_leasing_list_asset.scss';
import '../../assets/styles/infrastructure_leasing_post_requirement.scss';

const DRAFT_KEY = 'ile_post_requirement_draft_v1';
/** Survives refresh between “Post requirement” and “Submit Enquiry” so bulk requests include requirementId. */
const REQ_SUBMIT_SESSION_KEY = 'ile_pr_submitted_requirement_v1';
const REQ_SUBMIT_SESSION_MAX_MS = 45 * 60 * 1000;

function readStoredSubmittedRequirementId() {
  try {
    const raw = sessionStorage.getItem(REQ_SUBMIT_SESSION_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    const id = Number(o?.id);
    const ts = Number(o?.ts);
    if (!Number.isFinite(id) || id < 1) return null;
    if (!Number.isFinite(ts) || Date.now() - ts > REQ_SUBMIT_SESSION_MAX_MS) return null;
    return Math.trunc(id);
  } catch {
    return null;
  }
}

function writeStoredSubmittedRequirementId(id) {
  try {
    sessionStorage.setItem(
      REQ_SUBMIT_SESSION_KEY,
      JSON.stringify({ id: Math.trunc(Number(id)), ts: Date.now() })
    );
  } catch {
    /* ignore */
  }
}

function clearStoredSubmittedRequirementId() {
  try {
    sessionStorage.removeItem(REQ_SUBMIT_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

const SectionIcon = ({ children, variant }) => (
  <div className={`ile-pr-section__icon${variant === 'neutral' ? ' ile-pr-section__icon--neutral' : ''}`}>{children}</div>
);

const ASSET_CLASS_OPTIONS = [
  { id: 'commercial_property', label: 'Commercial Property', kind: 'property' },
  { id: 'warehouse', label: 'Warehouse', kind: 'property' },
  { id: 'factory_industrial', label: 'Factory / Industrial Land', kind: 'property' },
  { id: 'industrial_shed', label: 'Industrial Shed', kind: 'property' },
  { id: 'machinery', label: 'Machinery', kind: 'machinery' },
  { id: 'equipment_infra', label: 'Equipment & Infrastructure', kind: 'machinery' },
];

/** Same building icon as list-asset listing type cards (#EE6C25). */
const ListingTypeBuildingIcon = () => (
  <svg
    className="ile-listing-type-icon"
    viewBox="0 0 88 60"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="2" y1="56" x2="86" y2="56" />
      <rect x="4" y="32" width="20" height="24" rx="2" />
      <rect x="32" y="4" width="24" height="52" rx="2.5" />
      <line x1="36" y1="16" x2="52" y2="16" />
      <line x1="36" y1="25" x2="52" y2="25" />
      <line x1="36" y1="34" x2="52" y2="34" />
      <rect x="64" y="32" width="20" height="24" rx="2" />
    </g>
  </svg>
);

const POST_REQUIREMENT_LISTING_TYPE_OPTIONS = [
  { id: 'property', title: 'Property', sub: 'Office, Warehouse, Factory, Land.' },
  { id: 'machinery', title: 'Machinery / Asset', sub: 'Equipment, Machinery, Infrastructure.' },
];

/** Property asset classes — Size / Area + Compliance sections */
const ASSET_CLASS_IDS_SIZE_COMPLIANCE = [
  'commercial_property',
  'warehouse',
  'factory_industrial',
  'industrial_shed',
];

/** Reset when leaving property-based sections */
const PROPERTY_SIZE_COMPLIANCE_CLEAR = {
  minArea: '',
  maxArea: '',
  areaUnit: 'sqft',
  moreSpace: '',
  compFireSafety: false,
  compEnvironmental: false,
  compTradeLicense: false,
  compDrugPharma: false,
  compOccupancyCert: false,
  compPollutionClearance: false,
  compFssai: false,
  compFactoryLicense: false,
  utilHighPowerLoad: false,
  utilWaterConnection: false,
  utilParkingHeavy: false,
  utilHighCeiling: false,
  utilElevator: false,
  utilThreePhase: false,
  utilLoadingUnloading: false,
  utilRampAccess: false,
  utilAirConditioning: false,
  utilGeneratorBackup: false,
};

/** Reset when leaving machinery / equipment sections */
const MACHINERY_CAPACITY_CLEAR = {
  capacityPowerReq: '',
  technicalSpecifications: '',
  preferredBrands: '',
  conditionPreference: '',
};

const PROPERTY_PAYLOAD_KEYS = [
  'minArea',
  'maxArea',
  'areaUnit',
  'moreSpace',
  'compFireSafety',
  'compEnvironmental',
  'compTradeLicense',
  'compDrugPharma',
  'compOccupancyCert',
  'compPollutionClearance',
  'compFssai',
  'compFactoryLicense',
  'utilHighPowerLoad',
  'utilWaterConnection',
  'utilParkingHeavy',
  'utilHighCeiling',
  'utilElevator',
  'utilThreePhase',
  'utilLoadingUnloading',
  'utilRampAccess',
  'utilAirConditioning',
  'utilGeneratorBackup',
];

const MACHINERY_PAYLOAD_KEYS = ['capacityPowerReq', 'technicalSpecifications', 'preferredBrands', 'conditionPreference'];

/** Stored in DB as exactly `property` or `machinery` (aligned with list-asset `listingType`). */
function canonicalListingTypeFromAssetClass(assetClass) {
  const id = String(assetClass || '').trim() || 'commercial_property';
  const opt = ASSET_CLASS_OPTIONS.find((x) => x.id === id);
  return opt && opt.kind === 'machinery' ? 'machinery' : 'property';
}

function buildRequirementSubmitPayload(form, showSizeComplianceSections, capacitySectionVisible) {
  const out = { ...form };
  delete out._listingTitleHint;
  out.listingType = canonicalListingTypeFromAssetClass(out.assetClass);
  if (!showSizeComplianceSections) {
    PROPERTY_PAYLOAD_KEYS.forEach((k) => {
      delete out[k];
    });
  }
  if (!capacitySectionVisible) {
    MACHINERY_PAYLOAD_KEYS.forEach((k) => {
      delete out[k];
    });
  }
  return out;
}

const AREA_UNIT_OPTIONS = [
  { value: 'sqft', label: 'Square Feet' },
  { value: 'sqm', label: 'Square Meters' },
  { value: 'acre', label: 'Acres' },
];

/** Budget Type — matches listing/search UX (purchase, lease/rent, short-term, hourly) */
const RENTAL_RATE_OPTIONS = [
  { value: 'total_purchase', label: 'Total Price (For Purchase)' },
  { value: 'per_month', label: 'Per Month (For Lease/Rent)' },
  { value: 'per_day', label: 'Per Day (For Short-term Rental)' },
  { value: 'per_hour', label: 'Per Hour (For Hourly Rental)' },
];

/** Must match backend `RequirementMatchTier` sort order for grouped UI */
const REQUIREMENT_MATCH_TIER_ORDER = ['exact_city', 'state_region', 'country', 'asset_only'];

function requirementMatchTierBadgeClass(tier) {
  if (tier === 'exact_city') return 'ile-pr-match-badge--in_range';
  if (tier === 'state_region' || tier === 'country') return 'ile-pr-match-badge--near_range';
  return 'ile-pr-match-badge--flexible_unpriced';
}

function normalizeDraftForm(form) {
  const next = { ...form };
  if (!RENTAL_RATE_OPTIONS.some((o) => o.value === next.rentalRateType)) {
    next.rentalRateType = 'per_month';
  }
  /** Legacy enum conditionPreference → clear; list-asset uses condition_facility id strings */
  if (next.conditionPreference != null && String(next.conditionPreference).trim() !== '') {
    if (!/^\d+$/.test(String(next.conditionPreference).trim())) {
      next.conditionPreference = '';
    }
  }
  next.listingType = canonicalListingTypeFromAssetClass(next.assetClass);
  return next;
}

/** Same values/labels as Infrastructure Leasing list-asset (`/infrastructure-leasing/list-asset`). */
const TRANSACTION_TYPE_OPTIONS = [
  { value: 'lease', label: 'Lease' },
  { value: 'rent', label: 'Rent' },
  { value: 'sale', label: 'Sale' },
  { value: 'lease_to_own', label: 'Lease to Own' },
];

const defaultForm = (listingTitleHint) => ({
  assetClass: 'commercial_property',
  /** Persisted with draft/submit — mirrors list-asset (`property` | `machinery`). */
  listingType: 'property',
  assetCategory: '',
  subCategory: '',
  subSubCategory: '',
  otherNeeds: '',
  transactionType: 'lease',
  assetSizeCategory: 'medium',
  primaryCountryId: '',
  primaryStateId: '',
  primaryCityId: '',
  locationDetails: '',
  /** Legacy drafts only; UI uses a single location details field. */
  moreLocation: '',
  territoryFlexible: 'any',
  industryBusinessType: '',
  rentalRateType: 'per_month',
  budgetMin: '',
  budgetMax: '',
  budgetNegotiable: true,
  durationType: 'months',
  minDuration: '',
  maxDuration: '',
  projectBased: false,
  capacityPowerReq: '',
  technicalSpecifications: '',
  preferredBrands: '',
  /** Master `condition_facility_id` when set; same source as list-asset Condition field */
  conditionPreference: '',
  minArea: '',
  maxArea: '',
  areaUnit: 'sqft',
  moreSpace: '',
  requiredBy: '',
  urgency: 'flexible',
  compFireSafety: false,
  compEnvironmental: false,
  compTradeLicense: false,
  compDrugPharma: false,
  compOccupancyCert: false,
  compPollutionClearance: false,
  compFssai: false,
  compFactoryLicense: false,
  utilHighPowerLoad: false,
  utilWaterConnection: false,
  utilParkingHeavy: false,
  utilHighCeiling: false,
  utilElevator: false,
  utilThreePhase: false,
  utilLoadingUnloading: false,
  utilRampAccess: false,
  utilAirConditioning: false,
  utilGeneratorBackup: false,
  businessPurpose: '',
  description: '',
  specialConditions: '',
  allowBrokers: true,
  confidential: false,
  contactName: '',
  company: '',
  email: '',
  phone: '',
  relatedListingId: '',
  _listingTitleHint: listingTitleHint || '',
});

/** Prefer server draft fields but keep live contact / listing-context hints from `prev`. */
function mergeServerRequirementDraft(prev, serverForm, listingIdParam) {
  const s = serverForm && typeof serverForm === 'object' ? serverForm : {};
  const hint = prev._listingTitleHint || s._listingTitleHint || '';
  const base = normalizeDraftForm({
    ...defaultForm(hint),
    ...s,
    relatedListingId: listingIdParam ? String(listingIdParam) : prev.relatedListingId || s.relatedListingId || '',
    _listingTitleHint: hint,
  });
  return {
    ...base,
    contactName: str(base.contactName) ? base.contactName : prev.contactName,
    email: str(base.email) ? base.email : prev.email,
    company: str(base.company) ? base.company : prev.company,
    phone: str(base.phone) ? base.phone : prev.phone,
  };
}

function str(v) {
  if (v == null) return '';
  return String(v).trim();
}

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** `/post-requirement?new=1` (or `fresh=1`) — blank requirement; skip local + server draft (contact still from profile). */
function readStartFreshFromSearch() {
  if (typeof window === 'undefined') return false;
  try {
    const sp = new URLSearchParams(window.location.search);
    const truthy = (key) => {
      const x = sp.get(key);
      if (x == null) return false;
      const t = String(x).trim().toLowerCase();
      return t === '1' || t === 'true' || t === 'yes';
    };
    return truthy('new') || truthy('fresh');
  } catch {
    return false;
  }
}

function countDigits(s) {
  return String(s ?? '').replace(/\D/g, '').length;
}

function isValidEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s ?? '').trim());
}

/** Returns human-readable errors (empty array when valid). */
function validateRequirementForm(f, ctx) {
  const errors = [];
  const { listingType, showSizeComplianceSections, capacitySectionVisible, subSubTypesLength } = ctx;
  if (!ASSET_CLASS_OPTIONS.some((o) => o.id === str(f.assetClass))) {
    errors.push('Select a listing type.');
  }
  if (listingType !== 'property' && listingType !== 'machinery') {
    errors.push('Select a listing type (property or machinery).');
  }
  if (!str(f.assetCategory)) {
    errors.push('Select an asset category.');
  }
  if (listingType === 'machinery' && subSubTypesLength > 0 && !str(f.subSubCategory)) {
    errors.push('Select a sub-sub-category.');
  }
  if (!str(f.primaryCountryId)) {
    errors.push('Select a primary country.');
  }
  if (!str(f.primaryStateId)) {
    errors.push('Select a primary state.');
  }
  if (!str(f.primaryCityId)) {
    errors.push('Select a primary city.');
  }
  if (!RENTAL_RATE_OPTIONS.some((o) => o.value === str(f.rentalRateType))) {
    errors.push('Select a budget type.');
  }
  if (!str(f.budgetMin) || !str(f.budgetMax)) {
    errors.push('Enter both minimum and maximum budget (₹).');
  } else {
    const bMin = Number(f.budgetMin);
    const bMax = Number(f.budgetMax);
    if (!Number.isFinite(bMin) || !Number.isFinite(bMax) || bMin < 0 || bMax < 0 || bMin > bMax) {
      errors.push('Budget range must be valid numbers with minimum less than or equal to maximum.');
    }
  }
  if (!str(f.minDuration) || !str(f.maxDuration)) {
    errors.push('Enter both minimum and maximum duration.');
  } else {
    const dMin = Number(f.minDuration);
    const dMax = Number(f.maxDuration);
    if (!Number.isFinite(dMin) || !Number.isFinite(dMax) || dMin < 0 || dMax < 0 || dMin > dMax) {
      errors.push('Duration values must be valid with minimum less than or equal to maximum.');
    }
  }
  if (!str(f.requiredBy)) {
    errors.push('Select the date you need this by.');
  }
  if (!str(f.businessPurpose)) {
    errors.push('Add intended business purpose.');
  }
  if (!str(f.description) || str(f.description).length < 20) {
    errors.push('Detailed requirement description must be at least 20 characters.');
  }
  if (capacitySectionVisible && !str(f.capacityPowerReq)) {
    errors.push('Add capacity / power requirement.');
  }
  if (showSizeComplianceSections && !str(f.minArea)) {
    errors.push('Enter minimum area.');
  }
  if (!str(f.contactName)) {
    errors.push('Add contact person name.');
  }
  if (!isValidEmail(f.email)) {
    errors.push('Enter a valid email address.');
  }
  if (countDigits(f.phone) < 8) {
    errors.push('Enter a valid phone number (at least 8 digits).');
  }
  return errors;
}

function maxDurationMonthsForEnquiry(f) {
  const a = Number(f.maxDuration) || Number(f.minDuration) || 1;
  const b = Number(f.minDuration) || 1;
  const high = Math.max(a, b);
  const dt = f.durationType || 'months';
  if (dt === 'days') {
    return Math.max(1, Math.ceil(high / 30));
  }
  if (dt === 'weeks') {
    return Math.max(1, Math.ceil((high * 7) / 30));
  }
  if (dt === 'years') {
    return Math.max(1, Math.ceil(high * 12));
  }
  return Math.max(1, Math.ceil(high));
}

function formatBudgetLineForEnquiry(f) {
  const rt = RENTAL_RATE_OPTIONS.find((o) => o.value === f.rentalRateType)?.label || f.rentalRateType || '';
  return `₹${str(f.budgetMin)} – ₹${str(f.budgetMax)}${rt ? ` (${rt})` : ''}`;
}

function buildLocationSummaryForEnquiry(f, countries, states, cities) {
  const c = (countries || []).find((x) => String(x.id) === String(f.primaryCountryId))?.label || '';
  const s = (states || []).find((x) => String(x.id) === String(f.primaryStateId))?.label || '';
  const city = (cities || []).find((x) => String(x.id) === String(f.primaryCityId))?.label || '';
  const parts = [city, s, c].filter(Boolean);
  if (parts.length) {
    return parts.join(', ');
  }
  return str(f.locationDetails) || '—';
}

function buildBulkEnquiryPayload(f, summary, countries, states, cities, showSizeComplianceSections) {
  const assetLabel = summary.asset || ASSET_CLASS_OPTIONS.find((o) => o.id === f.assetClass)?.label || 'Requirement';
  return {
    asset: {
      name: `Buyer requirement — ${assetLabel}`,
      type: assetLabel,
      location: buildLocationSummaryForEnquiry(f, countries, states, cities),
    },
    enquiry: {
      startDate: str(f.requiredBy),
      durationMonths: maxDurationMonthsForEnquiry(f),
      budget: formatBudgetLineForEnquiry(f),
      areaRequired:
        showSizeComplianceSections && (str(f.minArea) || str(f.maxArea))
          ? `${str(f.minArea) || '—'} – ${str(f.maxArea) || '—'} ${f.areaUnit || ''}`.trim()
          : null,
      message: str(f.description),
      urgency: str(f.urgency) || 'flexible',
    },
    contact: {
      name: str(f.contactName),
      email: str(f.email).toLowerCase(),
      phone: str(f.phone),
      company: str(f.company) || null,
    },
  };
}

function SectionTitle({ title }) {
  return <h2 className="ile-pr-section__title">{title}</h2>;
}

function SectionHeading({ title, subtitle, icon, iconVariant = 'neutral' }) {
  return (
    <div className="ile-pr-section__head ile-pr-section__head--with-subtitle">
      <SectionIcon variant={iconVariant}>{icon}</SectionIcon>
      <div className="ile-pr-section__head-text">
        <h2 className="ile-pr-section__title">{title}</h2>
        {subtitle ? <p className="ile-pr-section__subtitle">{subtitle}</p> : null}
      </div>
    </div>
  );
}

export default function InfrastructureLeasingPostRequirementPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const listingIdParam = searchParams.get('listingId');
  const {
    countries,
    states,
    cities,
    loading: loadingCountries,
    loadingStates,
    loadingCities,
    updateListsForCountry,
    updateListsForState,
  } = useLocationMaster();
  const [refListing, setRefListing] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const savingDraftRef = useRef(false);
  const matchesAnchorRef = useRef(null);
  /** Delayed navigation to My requirements after toast feedback (cleared on unmount). */
  const myRequirementsRedirectTimerRef = useRef(null);
  const [submittingMatch, setSubmittingMatch] = useState(false);
  const [matchItems, setMatchItems] = useState([]);
  const [matchScanned, setMatchScanned] = useState(0);
  const [selectedMatchIds, setSelectedMatchIds] = useState(() => new Set());
  const [sortMatches, setSortMatches] = useState('updated');
  const [sendingBulk, setSendingBulk] = useState(false);
  /** Listing ids where enquiry POST succeeded (manual Submit Enquiry). */
  const [enquirySentOkIds, setEnquirySentOkIds] = useState(() => new Set());
  /** Listing ids where enquiry POST failed — buyer can retry via Submit Enquiry. */
  const [enquirySentFailIds, setEnquirySentFailIds] = useState(() => new Set());
  /** Latest submitted requirement id (same session) — links bulk enquiries to that requirement. */
  const [submittedRequirementId, setSubmittedRequirementId] = useState(null);
  /** Stable for this mount: user opened “new requirement” via `?new=1` — do not hydrate server draft later. */
  const [beganAsFreshStart] = useState(() => readStartFreshFromSearch());
  const [f, setF] = useState(() => {
    if (readStartFreshFromSearch()) {
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch {
        /* ignore */
      }
      return normalizeDraftForm(defaultForm(''));
    }
    const d = loadDraft();
    if (d && typeof d === 'object' && d.form && typeof d.form === 'object') {
      return normalizeDraftForm({ ...defaultForm(''), ...d.form });
    }
    return defaultForm('');
  });

  const listingType = useMemo(() => {
    const lt = String(f.listingType || '').trim();
    if (lt === 'machinery' || lt === 'property') {
      return lt;
    }
    const o = ASSET_CLASS_OPTIONS.find((x) => x.id === f.assetClass);
    return o && o.kind === 'machinery' ? 'machinery' : 'property';
  }, [f.listingType, f.assetClass]);

  const showSizeComplianceSections = ASSET_CLASS_IDS_SIZE_COMPLIANCE.includes(f.assetClass);
  /** Capacity / Specifications — Machinery & Equipment & Infrastructure only */
  const capacitySectionVisible = listingType === 'machinery';

  const { list: conditionFacilities, loading: loadingConditionFacility } = useMasterConditionFacility(
    listingType === 'machinery'
  );

  const { list: facilityTypes, loading: loadingF } = useMasterFacilityTypes(listingType);
  const { list: subTypes, loading: loadingSub } = useMasterFacilitySubTypes(f.assetCategory);
  const { list: subSubTypes, loading: loadingSubSub } = useMasterFacilitySubSubTypes(
    listingType === 'machinery' ? f.subCategory : null
  );

  const up = useCallback((patch) => {
    setF((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleListingTypeCardClick = useCallback(
    (kind) => {
      if (kind === 'property') {
        const currentIsProperty = ASSET_CLASS_OPTIONS.find((x) => x.id === f.assetClass)?.kind === 'property';
        const nextAssetClass = currentIsProperty ? f.assetClass : 'commercial_property';
        const switchingFromMachinery = !currentIsProperty;
        up({
          listingType: 'property',
          assetClass: nextAssetClass,
          ...(switchingFromMachinery ? { assetCategory: '', subCategory: '', subSubCategory: '' } : {}),
        });
      } else {
        const currentIsMachinery = ASSET_CLASS_OPTIONS.find((x) => x.id === f.assetClass)?.kind === 'machinery';
        const nextAssetClass = currentIsMachinery ? f.assetClass : 'machinery';
        const switchingFromProperty = !currentIsMachinery;
        up({
          listingType: 'machinery',
          assetClass: nextAssetClass,
          ...(switchingFromProperty ? { assetCategory: '', subCategory: '', subSubCategory: '' } : {}),
        });
      }
    },
    [f.assetClass, up]
  );

  const setField = (key) => (e) => {
    const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    up({ [key]: v });
  };

  const onAssetCategoryChange = (e) => {
    up({ assetCategory: e.target.value, subCategory: '', subSubCategory: '' });
  };

  const onSubCategoryChange = (e) => {
    up({ subCategory: e.target.value, subSubCategory: '' });
  };

  const prevAssetClassRef = useRef(null);

  /** Clear section-specific fields when leaving property vs machinery asset families */
  useEffect(() => {
    const curr = f.assetClass;
    const prev = prevAssetClassRef.current;
    if (prev === null) {
      prevAssetClassRef.current = curr;
      return;
    }
    if (prev === curr) {
      return;
    }

    const wasProp = ASSET_CLASS_IDS_SIZE_COMPLIANCE.includes(prev);
    const isProp = ASSET_CLASS_IDS_SIZE_COMPLIANCE.includes(curr);
    const wasMach = ASSET_CLASS_OPTIONS.find((x) => x.id === prev)?.kind === 'machinery';
    const isMach = ASSET_CLASS_OPTIONS.find((x) => x.id === curr)?.kind === 'machinery';

    const patch = {};
    if (wasProp && !isProp) {
      Object.assign(patch, PROPERTY_SIZE_COMPLIANCE_CLEAR);
    }
    if (wasMach && !isMach) {
      Object.assign(patch, MACHINERY_CAPACITY_CLEAR);
    }

    prevAssetClassRef.current = curr;

    if (Object.keys(patch).length > 0) {
      setF((p) => ({ ...p, ...patch }));
    }
  }, [f.assetClass]);

  /** Keep `listingType` in state aligned with `assetClass` for DB field `property` | `machinery`. */
  useEffect(() => {
    const canon = canonicalListingTypeFromAssetClass(f.assetClass);
    if (String(f.listingType || '').trim() !== canon) {
      setF((p) => ({ ...p, listingType: canon }));
    }
  }, [f.assetClass, f.listingType]);

  useEffect(() => {
    if (!StorageService.isAuthenticated()) {
      const ret = `${location.pathname}${location.search || ''}`;
      navigate('/login', { replace: true, state: { from: ret } });
    }
  }, [navigate, location.pathname, location.search]);

  const scheduleRedirectToMyRequirements = useCallback((delayMs) => {
    const ms = Number(delayMs);
    const safe = Number.isFinite(ms) && ms >= 800 ? ms : 2800;
    if (myRequirementsRedirectTimerRef.current != null) {
      clearTimeout(myRequirementsRedirectTimerRef.current);
    }
    myRequirementsRedirectTimerRef.current = setTimeout(() => {
      myRequirementsRedirectTimerRef.current = null;
      navigate('/infrastructure-leasing/my-requirements');
    }, safe);
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (myRequirementsRedirectTimerRef.current != null) {
        clearTimeout(myRequirementsRedirectTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (beganAsFreshStart) {
      clearStoredSubmittedRequirementId();
    }
  }, [beganAsFreshStart]);

  /** Strip `new` / `fresh` from URL after applying blank form (keep `listingId`). */
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const truthy = (key) => {
      const x = sp.get(key);
      if (x == null) return false;
      const t = String(x).trim().toLowerCase();
      return t === '1' || t === 'true' || t === 'yes';
    };
    if (!truthy('new') && !truthy('fresh')) {
      return;
    }
    const qs = new URLSearchParams();
    if (listingIdParam) {
      qs.set('listingId', listingIdParam);
    }
    const nextSearch = qs.toString();
    navigate(
      { pathname: '/infrastructure-leasing/post-requirement', search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true }
    );
  }, [location.search, listingIdParam, navigate]);

  useEffect(() => {
    if (!listingIdParam) {
      return;
    }
    let c = false;
    (async () => {
      try {
        const res = await fetchPublishedInfrastructureById(listingIdParam);
        if (c) {
          return;
        }
        if (res?.success && res?.data) {
          setRefListing(res.data);
          setF((prev) => ({
            ...prev,
            relatedListingId: String(listingIdParam),
            _listingTitleHint: res.data?.title || '',
          }));
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      c = true;
    };
  }, [listingIdParam]);

  useEffect(() => {
    const u = StorageService.getUser();
    if (!u) {
      return;
    }
    setF((p) => ({
      ...p,
      contactName: p.contactName || str(u.name),
      email: p.email || str(u.email),
      company: p.company || str(u.company_name),
      phone: p.phone || str(u.phone),
    }));
  }, []);

  /** If server draft is newer than this device’s saved copy, hydrate the form. */
  useEffect(() => {
    let cancelled = false;
    if (!StorageService.getToken()) {
      return undefined;
    }
    if (beganAsFreshStart) {
      return undefined;
    }
    (async () => {
      try {
        const res = await fetchPostRequirementDraftAPI();
        if (cancelled || !res?.success || !res?.data?.form) {
          return;
        }
        const serverUpdated = res.data.updatedAt ? Date.parse(res.data.updatedAt) : 0;
        const local = loadDraft();
        const localSaved = local?.savedAt ? Date.parse(local.savedAt) : 0;
        if (serverUpdated <= localSaved) {
          return;
        }
        setF((prev) => mergeServerRequirementDraft(prev, res.data.form, listingIdParam));
        try {
          localStorage.setItem(
            DRAFT_KEY,
            JSON.stringify({ form: res.data.form, savedAt: res.data.updatedAt })
          );
        } catch {
          /* ignore */
        }
      } catch {
        /* offline / errors — keep local state */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [listingIdParam, beganAsFreshStart]);

  useEffect(() => {
    updateListsForCountry(f.primaryCountryId || '');
  }, [f.primaryCountryId, updateListsForCountry]);

  useEffect(() => {
    updateListsForState(f.primaryCountryId || '', f.primaryStateId || '');
  }, [f.primaryCountryId, f.primaryStateId, updateListsForState]);

  const onPrimaryCountryChange = (e) => {
    const v = e.target.value;
    up({ primaryCountryId: v, primaryStateId: '', primaryCityId: '' });
  };

  const onPrimaryStateChange = (e) => {
    const v = e.target.value;
    up({ primaryStateId: v, primaryCityId: '' });
  };

  const summary = useMemo(() => {
    const ass = ASSET_CLASS_OPTIONS.find((a) => a.id === f.assetClass)?.label || f.assetClass;
    const transactionLabel =
      TRANSACTION_TYPE_OPTIONS.find((o) => o.value === f.transactionType)?.label || f.transactionType || '—';
    const assetSizeOpt = INFRA_ASSET_SIZE_OPTIONS.find((o) => o.id === f.assetSizeCategory);
    const assetSizeSummary = assetSizeOpt ? `${assetSizeOpt.title} (${assetSizeOpt.sub})` : f.assetSizeCategory || '—';
    const primaryCountryLabel =
      (countries || []).find((c) => String(c.id) === String(f.primaryCountryId))?.label ||
      (str(f.primaryCountryId) ? `Country #${f.primaryCountryId}` : '—');
    return {
      asset: ass,
      transaction: transactionLabel,
      assetSize: assetSizeSummary,
      primaryCountry: primaryCountryLabel,
      industry: str(f.industryBusinessType) || '—',
      budget:
        f.budgetMin || f.budgetMax
          ? `₹${f.budgetMin || '0'} – ₹${f.budgetMax || '∞'}`
          : '—',
      requiredBy: str(f.requiredBy) || '—',
      urgency:
        URGENCY_OPTIONS.find((o) => o.value === f.urgency)?.label || (str(f.urgency) ? f.urgency : '—'),
      areaRange: (() => {
        if (!str(f.minArea) && !str(f.maxArea)) {
          return '—';
        }
        const unitLabel = AREA_UNIT_OPTIONS.find((u) => u.value === f.areaUnit)?.label || '';
        return `${str(f.minArea) || '—'} – ${str(f.maxArea) || '—'} ${unitLabel}`.trim();
      })(),
    };
  }, [f, countries]);

  const budgetRateHint = useMemo(() => {
    switch (f.rentalRateType) {
      case 'total_purchase':
        return 'Total purchase price range';
      case 'per_month':
        return 'Monthly rate';
      case 'per_day':
        return 'Daily rate';
      case 'per_hour':
        return 'Hourly rate';
      default:
        return '';
    }
  }, [f.rentalRateType]);

  const durationFieldLabels = useMemo(() => {
    const u = { days: 'days', weeks: 'weeks', months: 'months', years: 'years' }[f.durationType] || 'period';
    return {
      min: `Minimum Duration (${u})`,
      max: `Maximum Duration (${u})`,
    };
  }, [f.durationType]);

  const sortedMatchItems = useMemo(() => {
    const arr = Array.isArray(matchItems) ? [...matchItems] : [];
    const tierRank = (x) => Number(x.matchTierRank ?? 99);
    if (sortMatches === 'price_low') {
      arr.sort((a, b) => {
        const d = tierRank(a) - tierRank(b);
        if (d !== 0) return d;
        return (a.rentAmount ?? Number.POSITIVE_INFINITY) - (b.rentAmount ?? Number.POSITIVE_INFINITY);
      });
    } else if (sortMatches === 'price_high') {
      arr.sort((a, b) => {
        const d = tierRank(a) - tierRank(b);
        if (d !== 0) return d;
        return (b.rentAmount ?? 0) - (a.rentAmount ?? 0);
      });
    } else {
      arr.sort((a, b) => {
        const d = tierRank(a) - tierRank(b);
        if (d !== 0) return d;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });
    }
    return arr;
  }, [matchItems, sortMatches]);

  const pendingSendCount = useMemo(
    () => [...selectedMatchIds].filter((id) => !enquirySentOkIds.has(id)).length,
    [selectedMatchIds, enquirySentOkIds]
  );

  const runBulkEnquiriesForListingIds = useCallback(
    async (listingIds) => {
      const enquiryPayload = buildBulkEnquiryPayload(
        f,
        summary,
        countries,
        states,
        cities,
        showSizeComplianceSections
      );
      const CHUNK = 40;
      const merged = [];
      for (let i = 0; i < listingIds.length; i += CHUNK) {
        const chunk = listingIds.slice(i, i + CHUNK);
        const bulkBody = {
          listingIds: chunk,
          enquiryPayload,
        };
        let reqIdForBulk = submittedRequirementId;
        if (reqIdForBulk == null || !Number.isFinite(Number(reqIdForBulk)) || Number(reqIdForBulk) < 1) {
          reqIdForBulk = readStoredSubmittedRequirementId();
        }
        if (reqIdForBulk != null && Number.isFinite(Number(reqIdForBulk)) && Number(reqIdForBulk) >= 1) {
          bulkBody.requirementId = Math.trunc(Number(reqIdForBulk));
        }
        const res = await postInfrastructureRequirementBulkEnquiriesAPI(bulkBody);
        if (res?.success === false) {
          throw Object.assign(new Error(res?.message || 'Could not send enquiries.'), { response: res });
        }
        merged.push(...(res?.data?.results || []));
      }
      return merged;
    },
    [f, summary, countries, states, cities, showSizeComplianceSections, submittedRequirementId]
  );

  const applyEnquiryResults = useCallback((results) => {
    setEnquirySentOkIds((prevOk) => {
      const nextOk = new Set(prevOk);
      for (const r of results) {
        if (r.ok) {
          nextOk.add(r.listingId);
        }
      }
      return nextOk;
    });
    setEnquirySentFailIds((prevFail) => {
      const nextFail = new Set(prevFail);
      for (const r of results) {
        if (r.ok) {
          nextFail.delete(r.listingId);
        } else {
          nextFail.add(r.listingId);
        }
      }
      return nextFail;
    });
  }, []);

  const toggleMatchSelect = (id) => {
    setSelectedMatchIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAllMatches = () => {
    setSelectedMatchIds(new Set(sortedMatchItems.map((x) => x.id)));
  };

  const clearMatchSelection = () => {
    setSelectedMatchIds(new Set());
  };

  const onSaveDraft = useCallback(async () => {
    if (savingDraftRef.current) {
      return;
    }
    savingDraftRef.current = true;
    setSavingDraft(true);
    /** Partial save: keep full form state (no submit-only field stripping). Ensures `listingType` is property|machinery. */
    const draftForm = normalizeDraftForm({ ...f });
    const savedAt = new Date().toISOString();
    const stored = { form: draftForm, savedAt };
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(stored));
    } catch {
      toast.error('Could not save draft in browser storage.');
      savingDraftRef.current = false;
      setSavingDraft(false);
      return;
    }
    try {
      const res = await savePostRequirementDraftAPI({ form: draftForm });
      if (res?.success !== false) {
        toast.success('Saved as Draft successfully');
      } else {
        toast.warning(res?.message || 'Saved on this device; server could not sync.');
      }
    } catch (e) {
      const msg = e?.response?.data?.message;
      if (e?.response?.status >= 400) {
        toast.error(msg || 'Server rejected the draft. Your copy is saved on this device only.');
      } else {
        toast.warning(msg || 'Draft saved on this device. Could not sync to server.');
      }
    } finally {
      savingDraftRef.current = false;
      setSavingDraft(false);
    }
  }, [f]);

  const onPost = async (e) => {
    e.preventDefault();
    const errors = validateRequirementForm(f, {
      listingType,
      showSizeComplianceSections,
      capacitySectionVisible,
      subSubTypesLength: subSubTypes.length,
    });
    if (errors.length > 0) {
      toast.error(errors[0], { autoClose: 6500 });
      if (errors.length > 1) {
        toast.warning(`${errors.length - 1} more issue(s) — please fix all required fields.`, { autoClose: 7500 });
      }
      return;
    }
    setSubmittingMatch(true);
    setSelectedMatchIds(new Set());
    setEnquirySentOkIds(new Set());
    setEnquirySentFailIds(new Set());
    const requirementPayload = buildRequirementSubmitPayload(f, showSizeComplianceSections, capacitySectionVisible);
    try {
      const submitRes = await submitPostRequirementAPI({ form: requirementPayload });
      if (!submitRes?.success) {
        toast.error(submitRes?.message || 'Could not submit requirement.');
        return;
      }
      const rid = submitRes?.data?.requirementId;
      if (rid != null && Number.isFinite(Number(rid)) && Number(rid) >= 1) {
        const ridN = Math.trunc(Number(rid));
        setSubmittedRequirementId(ridN);
        writeStoredSubmittedRequirementId(ridN);
      }
      toast.success('Requirement submitted successfully');

      const res = await postInfrastructureRequirementMatchAPI(requirementPayload);
      if (!res?.success) {
        toast.error(res?.message || 'Could not find matching listings.');
        setMatchItems([]);
        setMatchScanned(0);
        return;
      }
      const items = res?.data?.items ?? [];
      const scanned = res?.data?.scanned ?? 0;
      setMatchItems(items);
      setMatchScanned(scanned);

      if (items.length === 0) {
        toast.info('No matching listings found', { autoClose: 3200 });
        scheduleRedirectToMyRequirements(3200);
      } else {
        toast.success(
          `Found ${items.length} matching listing${items.length === 1 ? '' : 's'}. Select the supplier(s) you want below, then click Submit Enquiry.`,
          { autoClose: 6500 }
        );
        requestAnimationFrame(() => {
          matchesAnchorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        });
      }
    } catch (err) {
      const msg = err?.response?.data?.message;
      const status = err?.response?.status;
      if (status >= 400 && msg) {
        toast.error(msg);
      } else {
        toast.error(msg || err?.message || 'Submit or matching failed.');
      }
      setMatchItems([]);
      setMatchScanned(0);
    } finally {
      setSubmittingMatch(false);
    }
  };

  const onSendToSelected = async () => {
    if (sendingBulk) {
      return;
    }
    if (selectedMatchIds.size === 0) {
      toast.warning('Please select at least one listing before submitting your enquiry.', { autoClose: 6000 });
      return;
    }
    const errors = validateRequirementForm(f, {
      listingType,
      showSizeComplianceSections,
      capacitySectionVisible,
      subSubTypesLength: subSubTypes.length,
    });
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }
    const idsToSend = [...selectedMatchIds].filter((id) => !enquirySentOkIds.has(id));
    if (idsToSend.length === 0) {
      toast.info('Selected listings already have an enquiry sent.');
      return;
    }
    setSendingBulk(true);
    try {
      const results = await runBulkEnquiriesForListingIds(idsToSend);
      applyEnquiryResults(results);
      const ok = results.filter((r) => r.ok).length;
      const fail = results.length - ok;
      if (ok) {
        try {
          localStorage.removeItem(DRAFT_KEY);
        } catch {
          /* ignore */
        }
        const redirectDelay = fail > 0 ? 4200 : 3200;
        toast.success(
          `Successfully submitted ${ok} enquiry${ok === 1 ? '' : 'ies'} to the selected supplier${ok === 1 ? '' : 's'}${fail ? ` (${fail} could not be sent).` : '.'}`,
          { autoClose: Math.min(redirectDelay + 700, 6000) }
        );
        scheduleRedirectToMyRequirements(redirectDelay);
      } else {
        toast.error(results[0]?.error || 'No enquiries were sent.', { autoClose: 5500 });
      }
      setSelectedMatchIds(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Send failed.');
    } finally {
      setSendingBulk(false);
    }
  };

  if (!StorageService.getToken()) {
    return null;
  }

  return (
    <div className="seller-profile infrastructure-leasing-asset">
      <div className="ile-pr-page">
        <WebsiteTopBar />

        <div className="ile-pr-hero">
          <div className="ile-pr-hero__inner ile-pr-hero__top">
            <div className="ile-pr-hero__intro">
              <p className="ile-pr-hero__eyebrow">Infrastructure &amp; Asset Exchange</p>
              <h1 className="ile-pr-hero__title">Post your requirement</h1>
              {beganAsFreshStart ? (
                <p className="small text-muted mb-0 mt-2" role="status">
                  New requirement: form starts empty and saved drafts are not loaded. Your account contact details are filled in below.
                </p>
              ) : null}
            </div>
            <div className="ile-pr-hero__toolbar">
              <Link
                to="/infrastructure-leasing/post-requirement?new=1"
                className="btn btn-sm btn-outline-secondary"
                title="Clear form and start a new requirement"
              >
                New requirement
              </Link>
              <Link to="/infrastructure-leasing/listings" className="btn btn-sm btn-outline-secondary">
                View listings
              </Link>
              <Link to="/home" className="btn btn-sm btn-outline-secondary text-decoration-none">
                Back to home
              </Link>
              <button
                type="button"
                className="ile-pr-btn-ghost ile-pr-hero__btn-draft"
                onClick={onSaveDraft}
                disabled={savingDraft}
              >
                {savingDraft ? 'Saving…' : 'Save as draft'}
              </button>
              <button type="submit" form="ile-pr-form" className="ile-pr-btn-primary" disabled={submittingMatch}>
                {submittingMatch ? 'Finding matches…' : 'Post requirement'}
              </button>
            </div>
          </div>
          {listingIdParam && (refListing?.title || f._listingTitleHint) && (
            <div className="ile-pr-hero__inner">
              <p className="ile-pr-hero__context mb-0">
                <strong>Send enquiry for:</strong> {str(refListing?.title || f._listingTitleHint)}
                {listingIdParam && (
                  <span className="text-muted">
                    {' '}
                    (listing #{listingIdParam})
                  </span>
                )}
              </p>
            </div>
          )}
        </div>

        <form id="ile-pr-form" className="ile-pr-main" onSubmit={onPost} noValidate>
          {/* 1. What do you need */}
          <div className="ile-pr-section">
            <div className="ile-pr-section__head">
              <SectionIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l3 2" />
                </svg>
              </SectionIcon>
              <SectionTitle title="What do you need?" />
            </div>
            <p className="ile-mute small">Select a listing type, then choose your asset category below.</p>
            <div className="mb-3">
              <span className="ile-form-label d-block mb-2">Listing Type</span>
              <div className="ile-pick-row ile-pick-row--listing-type">
                {POST_REQUIREMENT_LISTING_TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`ile-pick-card ile-pick-card--listing${listingType === opt.id ? ' ile-pick-card--on' : ''}`}
                    onClick={() => handleListingTypeCardClick(opt.id)}
                  >
                    <span className="ile-pick-icon ile-pick-icon--listing" aria-hidden>
                      <ListingTypeBuildingIcon />
                    </span>
                    <span className="ile-pick-title">{opt.title}</span>
                    <span className="ile-pick-sub">{opt.sub}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="row g-2">
              <div className={`col-12 ${listingType === 'machinery' ? 'col-md-4' : 'col-md-6'}`}>
                <span className="ile-form-label">
                  Asset category <span className="text-danger">*</span>
                </span>
                <select
                  className="input-pill"
                  value={f.assetCategory}
                  onChange={onAssetCategoryChange}
                  disabled={loadingF}
                >
                  <option value="">{loadingF ? 'Loading…' : 'Select category'}</option>
                  {facilityTypes.map((row) => (
                    <option key={row.facility_type_id} value={String(row.facility_type_id)}>
                      {row.facility_type}
                    </option>
                  ))}
                </select>
              </div>
              <div className={`col-12 ${listingType === 'machinery' ? 'col-md-4' : 'col-md-6'}`}>
                <span className="ile-form-label">Subcategory</span>
                <select
                  className="input-pill"
                  value={f.subCategory}
                  onChange={onSubCategoryChange}
                  disabled={!f.assetCategory || loadingSub}
                >
                  <option value="">{!f.assetCategory ? 'Select category first' : loadingSub ? 'Loading…' : 'Select subcategory'}</option>
                  {subTypes.map((row) => (
                    <option key={row.facility_sub_type_id} value={String(row.facility_sub_type_id)}>
                      {row.facility_sub_type}
                    </option>
                  ))}
                </select>
              </div>
              {listingType === 'machinery' && (
                <div className="col-12 col-md-4">
                  <span className="ile-form-label">Sub-sub-category</span>
                  <select
                    className="input-pill"
                    value={f.subSubCategory}
                    onChange={setField('subSubCategory')}
                    disabled={!f.subCategory || loadingSubSub}
                    required={!loadingSubSub && subSubTypes.length > 0}
                    aria-required={subSubTypes.length > 0}
                  >
                    <option value="">
                      {!f.subCategory
                        ? 'Select subcategory first'
                        : loadingSubSub
                          ? 'Loading…'
                          : subSubTypes.length === 0
                            ? 'No further types'
                            : 'Select sub-sub-category'}
                    </option>
                    {subSubTypes.map((row) => (
                      <option key={row.facility_sub_sub_type_id} value={String(row.facility_sub_sub_type_id)}>
                        {row.facility_sub_sub_type}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="col-12">
                <span className="ile-form-label">Additional details / specific requirements</span>
                <textarea
                  className="input-pill"
                  style={{ minHeight: 100 }}
                  value={f.otherNeeds}
                  onChange={setField('otherNeeds')}
                  placeholder="E.g. grade A office, 24/7 access, VRV HVAC…"
                />
              </div>
              <div className="col-12 col-md-6">
                <span className="ile-form-label">Transaction type</span>
                <select className="input-pill" value={f.transactionType} onChange={setField('transactionType')}>
                  {TRANSACTION_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-6">
                <span className="ile-form-label">Asset size category</span>
                <select className="input-pill" value={f.assetSizeCategory} onChange={setField('assetSizeCategory')}>
                  {INFRA_ASSET_SIZE_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.title} ({o.sub})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 2. Location — International Location Preferences */}
          <div className="ile-pr-section ile-pr-section--elevated">
            <SectionHeading
              title="International Location Preferences"
              subtitle="Specify your preferred countries, states/provinces, and cities"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <circle cx="12" cy="10" r="3" />
                  <path d="M12 21.7C9 16 4 12.2 4 8.5A8 8 0 0 1 20 8.5c0 3.7-5 7.5-8 13.2z" />
                </svg>
              }
            />
            <div className="mb-3">
              <span className="ile-form-label">
                Primary Country <span className="text-danger">*</span>
              </span>
              <select
                className="input-pill"
                value={f.primaryCountryId}
                onChange={onPrimaryCountryChange}
                disabled={loadingCountries}
                required
                aria-required
              >
                <option value="">{loadingCountries ? 'Loading…' : 'Select primary country'}</option>
                {countries.map((c) => (
                  <option key={String(c.id)} value={String(c.id)}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="row g-2 mb-3">
              <div className="col-12 col-md-6">
                <span className="ile-form-label">
                  Preferred state / province <span className="text-danger">*</span>
                </span>
                <select
                  className="input-pill"
                  value={f.primaryStateId}
                  onChange={onPrimaryStateChange}
                  disabled={!f.primaryCountryId || loadingStates}
                >
                  <option value="">
                    {!f.primaryCountryId ? 'Select country first' : loadingStates ? 'Loading…' : 'Select state'}
                  </option>
                  {states.map((s) => (
                    <option key={String(s.id)} value={String(s.id)}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-6">
                <span className="ile-form-label">
                  Preferred city <span className="text-danger">*</span>
                </span>
                <select
                  className="input-pill"
                  value={f.primaryCityId}
                  onChange={setField('primaryCityId')}
                  disabled={!f.primaryStateId || loadingCities}
                >
                  <option value="">
                    {!f.primaryStateId ? 'Select state first' : loadingCities ? 'Loading…' : 'Select city'}
                  </option>
                  {cities.map((c) => (
                    <option key={String(c.id)} value={String(c.id)}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="ile-pr-field-hint small text-muted mb-3">
              Country, state, and city are required to post. Results are grouped by location fit: same city, same state, same country, then (if you
              enable flexible territory) similar assets in other countries.
            </p>
            <div className="mb-3">
              <span className="ile-form-label">Specific Area / Location Details (Optional)</span>
              <textarea
                className="input-pill"
                style={{ minHeight: 88 }}
                value={f.locationDetails}
                onChange={setField('locationDetails')}
                placeholder="e.g., Industrial Zone, Business District, Near Airport, etc."
              />
            </div>
            <div className="mb-3">
              <label className="ile-pr-checkbox ile-pr-checkbox--dark">
                <input
                  type="checkbox"
                  checked={f.territoryFlexible === 'any'}
                  onChange={(e) => up({ territoryFlexible: e.target.checked ? 'any' : 'strict' })}
                />
                I&apos;m flexible with location and open to nearby areas / regions
              </label>
              <p className="small text-muted mb-0 mt-1 ps-4 ms-1">
                When enabled, matches include a final group: same listing type and category outside your country (listed after same city, state, and
                country matches).
              </p>
            </div>
            <div>
              <span className="ile-form-label">Industry / Business Type</span>
              <input
                className="input-pill"
                type="text"
                value={f.industryBusinessType}
                onChange={setField('industryBusinessType')}
                placeholder="e.g., E-commerce, Manufacturing, Logistics, Pharma, Healthcare"
                autoComplete="off"
              />
            </div>
          </div>

          {/* 3. Budget */}
          <div className="ile-pr-section ile-pr-section--elevated">
            <SectionHeading
              title="Budget Range"
              subtitle={"What's your budget for this requirement?"}
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              }
            />
            <div className="row g-3 align-items-start">
              <div className="col-12 col-md-4">
                <span className="ile-form-label">
                  Budget Type <span className="text-danger">*</span>
                </span>
                <select className="input-pill" value={f.rentalRateType} onChange={setField('rentalRateType')}>
                  {RENTAL_RATE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-4">
                <span className="ile-form-label">
                  Minimum budget (₹) <span className="text-danger">*</span>
                </span>
                <input
                  className="input-pill"
                  type="number"
                  min="0"
                  value={f.budgetMin}
                  onChange={setField('budgetMin')}
                  inputMode="numeric"
                  placeholder="500000"
                />
                {budgetRateHint ? <p className="ile-pr-field-hint mb-0">{budgetRateHint}</p> : null}
              </div>
              <div className="col-12 col-md-4">
                <span className="ile-form-label">
                  Maximum budget (₹) <span className="text-danger">*</span>
                </span>
                <input
                  className="input-pill"
                  type="number"
                  min="0"
                  value={f.budgetMax}
                  onChange={setField('budgetMax')}
                  inputMode="numeric"
                  placeholder="2000000"
                />
              </div>
              <div className="col-12">
                <label className="ile-pr-checkbox ile-pr-checkbox--dark">
                  <input type="checkbox" checked={f.budgetNegotiable} onChange={setField('budgetNegotiable')} />
                  Budget is negotiable
                </label>
              </div>
            </div>
          </div>

          {/* 4. Duration */}
          <div className="ile-pr-section ile-pr-section--elevated">
            <SectionHeading
              title="Lease / Rental Duration"
              subtitle="How long do you need the asset for?"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
              }
            />
            <div className="row g-3 align-items-start">
              <div className="col-12 col-md-4">
                <span className="ile-form-label">Duration Type</span>
                <select className="input-pill" value={f.durationType} onChange={setField('durationType')}>
                  {DURATION_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-4">
                <span className="ile-form-label">{durationFieldLabels.min}</span>
                <input
                  className="input-pill"
                  type="number"
                  min="0"
                  value={f.minDuration}
                  onChange={setField('minDuration')}
                  placeholder="3"
                />
              </div>
              <div className="col-12 col-md-4">
                <span className="ile-form-label">
                  {durationFieldLabels.max} <span className="text-danger">*</span>
                </span>
                <input
                  className="input-pill"
                  type="number"
                  min="0"
                  value={f.maxDuration}
                  onChange={setField('maxDuration')}
                  placeholder="24"
                />
              </div>
              <div className="col-12">
                <label className="ile-pr-checkbox ile-pr-checkbox--dark">
                  <input type="checkbox" checked={f.projectBased} onChange={setField('projectBased')} />
                  Duration is flexible and can be extended
                </label>
              </div>
            </div>
            <div className="ile-pr-note ile-pr-note--example">
              <strong>Example:</strong> If you need equipment for 3-6 months, set minimum as 3 and maximum as 6 months. For daily rentals (like
              construction equipment), use days.
            </div>
          </div>

          {/* 5. Timeline */}
          <div className="ile-pr-section ile-pr-section--elevated">
            <SectionHeading
              title="Timeline"
              subtitle="When do you need this?"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
              }
            />
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <span className="ile-form-label">
                  Required by date <span className="text-danger">*</span>
                </span>
                <input
                  className="input-pill"
                  type="date"
                  value={f.requiredBy}
                  onChange={setField('requiredBy')}
                />
              </div>
              <div className="col-12 col-md-6">
                <span className="ile-form-label">Urgency Level</span>
                <select className="input-pill" value={f.urgency} onChange={setField('urgency')}>
                  {URGENCY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 6. Capacity / Specifications — Machinery & Equipment & Infrastructure only */}
          {capacitySectionVisible && (
            <div className="ile-pr-section ile-pr-section--elevated">
              <SectionHeading
                title="Capacity / Specifications"
                subtitle="What are your technical requirements?"
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                  </svg>
                }
                iconVariant="neutral"
              />
              <div className="row g-3">
                <div className="col-12">
                  <span className="ile-form-label">
                    Capacity / Power Requirement
                    {capacitySectionVisible ? <span className="text-danger"> *</span> : null}
                  </span>
                  <input
                    className="input-pill"
                    value={f.capacityPowerReq}
                    onChange={setField('capacityPowerReq')}
                    placeholder="e.g., 125 KVA, 20 Ton capacity, 100 HP"
                  />
                </div>
                <div className="col-12">
                  <span className="ile-form-label">Technical Specifications</span>
                  <textarea
                    className="input-pill"
                    style={{ minHeight: 120 }}
                    value={f.technicalSpecifications}
                    onChange={setField('technicalSpecifications')}
                    placeholder="Detailed technical requirements, features needed, compatibility requirements"
                  />
                </div>
                <div className="col-12 col-md-6">
                  <span className="ile-form-label">Preferred Brands (if any)</span>
                  <input
                    className="input-pill"
                    value={f.preferredBrands}
                    onChange={setField('preferredBrands')}
                    placeholder="e.g., Caterpillar, Kirloskar, Cummins"
                  />
                </div>
                <div className="col-12 col-md-6">
                  <span className="ile-form-label">Condition Preference</span>
                  <select
                    className="input-pill"
                    value={f.conditionPreference}
                    onChange={setField('conditionPreference')}
                    disabled={loadingConditionFacility}
                  >
                    <option value="">{loadingConditionFacility ? 'Loading…' : 'Select condition'}</option>
                    {conditionFacilities.map((row) => (
                      <option key={row.condition_facility_id} value={String(row.condition_facility_id)}>
                        {row.condition_facility}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Size / Area + Compliance — Commercial Property, Warehouse, Factory / Industrial Land */}
          {showSizeComplianceSections && (
            <>
              <div className="ile-pr-section ile-pr-section--elevated ile-pr-section--size-area">
                <SectionHeading
                  title="Size / Area Requirement"
                  subtitle="How much space do you need?"
                  icon={
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <path d="M3 9h18M9 21V9" />
                    </svg>
                  }
                  iconVariant="neutral"
                />
                <div className="row g-3 align-items-end">
                  <div className="col-12 col-md-4">
                    <span className="ile-form-label">
                      Minimum Area <span className="text-danger">*</span>
                    </span>
                    <input
                      className="input-pill"
                      type="number"
                      min="0"
                      value={f.minArea}
                      onChange={setField('minArea')}
                      placeholder="5000"
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <span className="ile-form-label">Maximum Area</span>
                    <input
                      className="input-pill"
                      type="number"
                      min="0"
                      value={f.maxArea}
                      onChange={setField('maxArea')}
                      placeholder="10000"
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <span className="ile-form-label">Unit</span>
                    <select className="input-pill" value={f.areaUnit} onChange={setField('areaUnit')}>
                      {AREA_UNIT_OPTIONS.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <span className="ile-form-label">Additional Size/Space Details</span>
                    <textarea
                      className="input-pill"
                      style={{ minHeight: 100 }}
                      value={f.moreSpace}
                      onChange={setField('moreSpace')}
                      placeholder="e.g., Need minimum 20 ft height, loading dock required, divisible space preferred"
                    />
                  </div>
                </div>
              </div>

              <div className="ile-pr-section ile-pr-section--elevated ile-pr-section--compliance-util">
                <SectionHeading
                  title="Compliance & Utility Requirements"
                  subtitle="What approvals and utilities do you need?"
                  icon={
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path d="M9 11l3 3L22 4" />
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                    </svg>
                  }
                  iconVariant="neutral"
                />
                <div className="ile-pr-compliance-sub">
                  <p className="ile-pr-compliance-sub__title">Required Compliance Approvals</p>
                  <div className="row g-2 ile-pr-check-grid">
                    <div className="col-12 col-md-6">
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.compFireSafety} onChange={setField('compFireSafety')} />
                        <span>Fire Safety Compliance</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.compEnvironmental} onChange={setField('compEnvironmental')} />
                        <span>Environmental Clearance</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.compTradeLicense} onChange={setField('compTradeLicense')} />
                        <span>Trade License</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.compDrugPharma} onChange={setField('compDrugPharma')} />
                        <span>Drug License (for pharma)</span>
                      </label>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.compOccupancyCert} onChange={setField('compOccupancyCert')} />
                        <span>Occupancy Certificate</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.compPollutionClearance} onChange={setField('compPollutionClearance')} />
                        <span>Pollution Clearance</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.compFssai} onChange={setField('compFssai')} />
                        <span>FSSAI Approval (for food)</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.compFactoryLicense} onChange={setField('compFactoryLicense')} />
                        <span>Factory License</span>
                      </label>
                    </div>
                  </div>
                </div>
                <hr className="ile-pr-compliance-split" />
                <div className="ile-pr-compliance-sub">
                  <p className="ile-pr-compliance-sub__title">Essential Utilities</p>
                  <div className="row g-2 ile-pr-check-grid">
                    <div className="col-12 col-md-6">
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.utilHighPowerLoad} onChange={setField('utilHighPowerLoad')} />
                        <span>High Power Load (100+ KW)</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.utilWaterConnection} onChange={setField('utilWaterConnection')} />
                        <span>Water Connection</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.utilParkingHeavy} onChange={setField('utilParkingHeavy')} />
                        <span>Parking for Heavy Vehicles</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.utilHighCeiling} onChange={setField('utilHighCeiling')} />
                        <span>High Ceiling (20+ ft)</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.utilElevator} onChange={setField('utilElevator')} />
                        <span>Elevator/Lift</span>
                      </label>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.utilThreePhase} onChange={setField('utilThreePhase')} />
                        <span>Three Phase Power</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.utilLoadingUnloading} onChange={setField('utilLoadingUnloading')} />
                        <span>Loading/Unloading Facility</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.utilRampAccess} onChange={setField('utilRampAccess')} />
                        <span>Ramp Access</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.utilAirConditioning} onChange={setField('utilAirConditioning')} />
                        <span>Air Conditioning</span>
                      </label>
                      <label className="ile-pr-check-row">
                        <input type="checkbox" checked={f.utilGeneratorBackup} onChange={setField('utilGeneratorBackup')} />
                        <span>Generator Backup</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* 8. Business purpose & details */}
          <div className="ile-pr-section ile-pr-section--elevated ile-pr-section--purpose">
            <SectionHeading
              title="Business Purpose & Details"
              subtitle="Help suppliers understand your needs better"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                </svg>
              }
              iconVariant="neutral"
            />
            <div className="ile-pr-purpose-fields">
              <div className="ile-pr-purpose-field">
                <span className="ile-form-label">
                  Intended Business Purpose <span className="text-danger">*</span>
                </span>
                <input
                  className="input-pill"
                  type="text"
                  value={f.businessPurpose}
                  onChange={setField('businessPurpose')}
                  placeholder="e.g., Setting up e-commerce fulfillment center, Manufacturing automotive parts, Food processing unit"
                  autoComplete="off"
                />
              </div>
              <div className="ile-pr-purpose-field">
                <span className="ile-form-label">
                  Detailed Requirement Description <span className="text-danger">*</span>
                </span>
                <textarea
                  className="input-pill"
                  style={{ minHeight: 112 }}
                  value={f.description}
                  onChange={setField('description')}
                  placeholder="Provide comprehensive details about your requirement. Include any specific needs, preferences, deal-breakers, or special conditions."
                />
                <p className="ile-pr-field-hint ile-pr-purpose-hint">
                  The more details you provide, the better proposals you&apos;ll receive
                </p>
              </div>
              <div className="ile-pr-purpose-field">
                <span className="ile-form-label">Additional Requirements / Special Conditions</span>
                <input
                  className="input-pill"
                  type="text"
                  value={f.specialConditions}
                  onChange={setField('specialConditions')}
                  placeholder="e.g., Need parking for 50 vehicles, Must have cold storage facility, Prefer properties near highway"
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          {/* 9. Privacy & response preferences */}
          <div className="ile-pr-section ile-pr-section--elevated ile-pr-section--privacy">
            <SectionHeading
              title="Privacy & Response Preferences"
              subtitle="Control who can see and respond to your requirement"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              }
              iconVariant="neutral"
            />
            <div className="ile-pr-privacy-options">
              <label className="ile-pr-privacy-card">
                <input type="checkbox" checked={f.allowBrokers} onChange={setField('allowBrokers')} />
                <div className="ile-pr-privacy-card__body">
                  <span className="ile-pr-privacy-card__title">Allow Brokers / Agents to Respond</span>
                  <span className="ile-pr-privacy-card__desc">
                    Brokers can also see and respond to your requirement. This increases your chances of finding suitable options.
                  </span>
                </div>
              </label>
              <label className="ile-pr-privacy-card">
                <input type="checkbox" checked={f.confidential} onChange={setField('confidential')} />
                <div className="ile-pr-privacy-card__body">
                  <span className="ile-pr-privacy-card__title">Confidential Mode</span>
                  <span className="ile-pr-privacy-card__desc">
                    Hide your company name and contact details from public view. Only verified suppliers who send proposals will see your
                    details.
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* 10. Contact */}
          <div className="ile-pr-section ile-pr-section--elevated">
            <SectionHeading
              title="Contact Information"
              subtitle="How suppliers can reach you"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              }
              iconVariant="neutral"
            />
            <div className="row g-2">
              <div className="col-12 col-md-6">
                <span className="ile-form-label">Contact person *</span>
                <input className="input-pill" value={f.contactName} onChange={setField('contactName')} autoComplete="name" />
              </div>
              <div className="col-12 col-md-6">
                <span className="ile-form-label">Company name</span>
                <input className="input-pill" value={f.company} onChange={setField('company')} autoComplete="organization" />
              </div>
              <div className="col-12 col-md-6">
                <span className="ile-form-label">Email *</span>
                <input
                  className="input-pill"
                  type="email"
                  value={f.email}
                  onChange={setField('email')}
                  autoComplete="email"
                />
              </div>
              <div className="col-12 col-md-6">
                <span className="ile-form-label">
                  Phone <span className="text-danger">*</span>
                </span>
                <input className="input-pill" type="tel" value={f.phone} onChange={setField('phone')} autoComplete="tel" />
              </div>
            </div>
          </div>

          <div className="ile-pr-summary">
            <strong>Requirement summary</strong>
            <ul>
              <li>Asset: {summary.asset}</li>
              <li>Transaction: {summary.transaction}</li>
              <li>Asset size: {summary.assetSize}</li>
              <li>Primary country: {summary.primaryCountry}</li>
              <li>Industry / business: {summary.industry}</li>
              <li>Budget: {summary.budget}</li>
              <li>Required by: {summary.requiredBy}</li>
              <li>Urgency: {summary.urgency}</li>
              {showSizeComplianceSections && <li>Area: {summary.areaRange}</li>}
              {f.relatedListingId && <li>Related listing id: {f.relatedListingId}</li>}
            </ul>
          </div>
        </form>

        <section ref={matchesAnchorRef} className="ile-pr-matches" aria-label="Matching listings">
          <div className="ile-pr-matches__inner">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h2 className="h5 mb-0">Matching listings</h2>
                <p className="ile-mute small mb-0">
                  {matchItems.length > 0
                    ? enquirySentOkIds.size > 0
                      ? `${enquirySentOkIds.size} enquiry${enquirySentOkIds.size === 1 ? '' : 'ies'} sent${
                          enquirySentFailIds.size ? `; ${enquirySentFailIds.size} failed (retry below)` : ''
                        }. Showing ${matchItems.length} match${matchItems.length === 1 ? '' : 'es'} from ${matchScanned} published assets — grouped by location priority (best match → broader).`
                      : `${matchItems.length} match${matchItems.length === 1 ? '' : 'es'} (from ${matchScanned} recent published assets). Select one or more listings, then click Submit Enquiry — nothing is sent until you confirm.`
                    : matchScanned > 0
                      ? `Last search checked ${matchScanned} recent published listing${matchScanned === 1 ? '' : 's'}; none matched listing type and asset category in your allowed regions (see note below). Enable flexible territory or adjust asset selections.`
                      : 'Complete the form and click Post requirement — matches are ordered by asset fit and location (city → state → country → optional other regions). You choose who to contact; use Submit Enquiry to send requests.'}
                </p>
              </div>
              {sortedMatchItems.length > 0 ? (
                <label className="d-flex align-items-center gap-2 small mb-0">
                  <span className="text-muted text-nowrap">Sort</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ minWidth: 168 }}
                    value={sortMatches}
                    onChange={(e) => setSortMatches(e.target.value)}
                  >
                    <option value="updated">Recently updated</option>
                    <option value="price_low">Price: low to high</option>
                    <option value="price_high">Price: high to low</option>
                  </select>
                </label>
              ) : null}
            </div>

            {enquirySentOkIds.size > 0 ? (
              <div className="alert alert-success py-2 px-3 small mb-3" role="status">
                <strong>Requests sent.</strong>{' '}
                {enquirySentOkIds.size} supplier{enquirySentOkIds.size === 1 ? '' : 's'} received your enquiry
                {enquirySentFailIds.size
                  ? `; ${enquirySentFailIds.size} could not be notified — select those listings and click Submit Enquiry to retry.`
                  : '.'}{' '}
                <Link to="/infrastructure-leasing/my-enquiries" className="alert-link">
                  View my enquiries
                </Link>
              </div>
            ) : null}

            {sortedMatchItems.length > 0 ? (
              <div className="mb-3">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={selectAllMatches}>
                    Select all
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearMatchSelection}>
                    Clear selection
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={onSendToSelected}
                    disabled={sendingBulk || submittingMatch}
                    aria-disabled={sendingBulk || submittingMatch}
                  >
                    {sendingBulk
                      ? 'Submitting…'
                      : pendingSendCount > 0
                        ? `Submit Enquiry (${pendingSendCount})`
                        : 'Submit Enquiry'}
                  </button>
                </div>
                <p className="small text-muted mb-0 mt-2">
                  Use the checkboxes on each card to choose suppliers. Submit Enquiry sends your request only to the selected listings (at least one
                  required).
                </p>
              </div>
            ) : null}

            <div className="ile-pr-match-tiers">
              {REQUIREMENT_MATCH_TIER_ORDER.map((tierKey) => {
                const groupItems = sortedMatchItems.filter((x) => String(x.matchTier || '') === tierKey);
                if (!groupItems.length) {
                  return null;
                }
                const heading = groupItems[0].matchTierLabel || tierKey;
                return (
                  <div key={tierKey} className="ile-pr-match-tier mb-4">
                    <h3 className="h6 fst-normal text-secondary mb-3 pb-2 border-bottom">{heading}</h3>
                    <div className="row g-3">
                      {groupItems.map((item) => (
                        <div key={item.id} className="col-12 col-md-6 col-xl-4">
                          <div
                            className={`ile-pr-match-card card h-100${selectedMatchIds.has(item.id) ? ' ile-pr-match-card--selected' : ''}`}
                          >
                            <div className="row g-0 align-items-stretch">
                              <div className="col-4">
                                <div className="ile-pr-match-card__img-wrap">
                                  <img
                                    src={
                                      item.imagePath
                                        ? resolvePublicFileUrl(item.imagePath)
                                        : 'https://via.placeholder.com/400x240?text=Listing'
                                    }
                                    alt=""
                                    className="ile-pr-match-card__img"
                                  />
                                </div>
                              </div>
                              <div className="col-8">
                                <div className="card-body py-2 px-3 d-flex flex-column h-100">
                                  <div className="form-check mb-1">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={enquirySentOkIds.has(item.id) || selectedMatchIds.has(item.id)}
                                      disabled={enquirySentOkIds.has(item.id) || submittingMatch}
                                      onChange={() => toggleMatchSelect(item.id)}
                                      id={`ile-pr-match-${item.id}`}
                                    />
                                    <label className="form-check-label small fw-semibold" htmlFor={`ile-pr-match-${item.id}`}>
                                      {enquirySentOkIds.has(item.id)
                                        ? 'Enquiry sent'
                                        : enquirySentFailIds.has(item.id)
                                          ? 'Retry enquiry'
                                          : 'Include in enquiry'}
                                    </label>
                                  </div>
                                  <span
                                    className={`badge align-self-start mb-1 ile-pr-match-badge ${requirementMatchTierBadgeClass(item.matchTier)}`}
                                  >
                                    {item.matchTierLabel || 'Match'}
                                  </span>
                                  <h3 className="h6 mb-1">{item.title}</h3>
                                  <p className="small text-muted mb-1" style={{ lineHeight: 1.35 }}>
                                    {item.locationLine}
                                  </p>
                                  <p className="small fw-semibold mb-2">{item.pricePrimary}</p>
                                  <div className="mt-auto">
                                    <Link
                                      to={`/infrastructure-leasing/listing/${item.id}`}
                                      className="btn btn-sm btn-outline-secondary"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      View listing
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {!submittingMatch && matchItems.length === 0 && matchScanned === 0 ? (
              <div className="ile-pr-matches-empty text-center py-4 px-2 rounded border bg-white text-muted small">
                No matches loaded yet. Complete the form and click Post requirement to search published assets.
              </div>
            ) : null}
            {!submittingMatch && matchItems.length === 0 && matchScanned > 0 ? (
              <div className="ile-pr-matches-empty text-center py-4 px-2 rounded border bg-white text-muted small">
                No listings matched your listing type and asset category (with your territory rules). Enable &quot;flexible territory&quot; to include
                similar assets in other countries, or adjust category / type and post again.
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}
